import React from 'react';
import { Container, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Loader, TablePaginationWrapper, ReloadIconButton, LocationStableSelect } from '../../elements';
import NoDataMessage from '../shared/NoDataMessage';
import { LocationsService } from '../../hooks/LocationsService';
import useFeedRegistrations from '../../hooks/useFeedRegistrations';
import { LocationPosition, LocationStable, LocationStableGroup } from '../../models/locations';
import { DEFAULT_LOCATION_PERIOD, FeedPeriod, feedPeriodRange } from '../../models/feed-registration';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';
import FeedPeriodSelect from './FeedPeriodSelect';
import FeedPeriodSummary from './FeedPeriodSummary';
import FeedRegistrationsTable from './FeedRegistrationsTable';

type RowScope = 'all' | LocationPosition.Left | LocationPosition.Right;

/**
 * Feed registered for a room, or for one row within it.
 *
 * A room is not an entity here - it is a free-text field on each pen - so there is no room id to
 * query by. The scope is resolved client-side into the pen ids it covers, and those go to the API as
 * `LocationIds`. That is also why the room list comes from the locations hierarchy rather than from a
 * rooms endpoint.
 */
const RoomFeedPanel: React.FC = () => {
  const { t } = useTranslation();

  const [stable, setStable] = React.useState<LocationStable>(LocationStable.Farrowing);
  const [stables, setStables] = React.useState<LocationStableGroup[]>([]);
  const [hierarchyBusy, setHierarchyBusy] = React.useState(false);
  const [hierarchyLoaded, setHierarchyLoaded] = React.useState(false);

  const [room, setRoom] = React.useState<string>('');
  const [rowScope, setRowScope] = React.useState<RowScope>('all');
  const [period, setPeriod] = React.useState<FeedPeriod>(DEFAULT_LOCATION_PERIOD);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  React.useEffect(() => {
    const service = new LocationsService();
    // The stable filter can change while the hierarchy is in flight; ignore all but the newest.
    let cancelled = false;

    setHierarchyBusy(true);

    service
      .getLocationsHierarchy(stable, 0)
      .then((result) => {
        if (cancelled) return;
        setStables(result?.stables ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setStables([]);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setHierarchyBusy(false);
        setHierarchyLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [stable, t]);

  const rooms = React.useMemo(() => {
    const names = new Set<string>();

    for (const group of stables) {
      for (const roomGroup of group.rooms) {
        if (roomGroup.room) names.add(roomGroup.room);
      }
    }

    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [stables]);

  // Keep a selection the current stable filter no longer offers from silently querying old pens.
  React.useEffect(() => {
    if (room && !rooms.includes(room)) {
      setRoom('');
      setPage(0);
    }
  }, [rooms, room]);

  const locationIds = React.useMemo(() => {
    if (!room) return [];

    return stables
      .flatMap((group) => group.rooms)
      .filter((roomGroup) => roomGroup.room === room)
      .flatMap((roomGroup) => roomGroup.locations)
      .filter((pen) => rowScope === 'all' || pen.positioning === rowScope)
      .map((pen) => pen.id)
      .filter(Boolean);
  }, [stables, room, rowScope]);

  const { startDate, endDate } = feedPeriodRange(period);

  const { registrations, totalCount, isBusy, loaded, reload } = useFeedRegistrations({
    locationIds,
    startDate,
    endDate,
    page,
    size: rowsPerPage
  });

  const resetPage = () => setPage(0);

  const changePeriod = (next: FeedPeriod) => {
    setPeriod(next);
    resetPage();
  };

  if (hierarchyBusy && !hierarchyLoaded) {
    return <Loader isLoading size={LoaderSize.MEDIUM} />;
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-900 m-0">{t('f-feed-reg-room-title')}</h2>
            <div className="flex items-center gap-3">
              <FeedPeriodSummary registrations={registrations} totalCount={totalCount} />
              <FeedPeriodSelect value={period} onChange={changePeriod} disabled={isBusy} />
              <ReloadIconButton action={reload} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <LocationStableSelect
              value={stable}
              cssClass="!min-w-48"
              onLocationStableChange={(value) => {
                setStable(value as unknown as LocationStable);
                // Room names belong to a stable. Keeping the old one would select no pens and
                // render an empty table, which reads as "never fed" rather than "nothing picked".
                setRoom('');
                resetPage();
              }}
            />

            <FormControl variant="outlined" size="small" className="!min-w-48">
              <InputLabel id="feedRoomLabel">{t('f-loc-room')}</InputLabel>
              <Select
                labelId="feedRoomLabel"
                id="feedRoomSelect"
                label={t('f-loc-room')}
                value={room}
                disabled={rooms.length === 0}
                onChange={(event) => {
                  setRoom(event.target.value as string);
                  resetPage();
                }}
              >
                {rooms.length === 0 ? (
                  <MenuItem value="" disabled>
                    {t('f-feed-reg-no-rooms')}
                  </MenuItem>
                ) : (
                  rooms.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={rowScope}
              onChange={(_event, value: RowScope | null) => {
                if (value === null) return;
                setRowScope(value);
                resetPage();
              }}
            >
              <ToggleButton value="all">{t('f-feed-reg-row-all')}</ToggleButton>
              <ToggleButton value={LocationPosition.Left}>{t('f-loc-pos-l')}</ToggleButton>
              <ToggleButton value={LocationPosition.Right}>{t('f-loc-pos-r')}</ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <div className="p-2">
          {room ? (
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                resetPage();
              }}
            >
              <FeedRegistrationsTable registrations={registrations} isBusy={isBusy} loaded={loaded} showPen />
            </TablePaginationWrapper>
          ) : (
            <NoDataMessage text={t('f-feed-reg-pick-room')} description={t('f-feed-reg-pick-room-info')} />
          )}
        </div>
      </div>
    </Container>
  );
};

export default RoomFeedPanel;
